import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, MapPin, Loader2, RefreshCw, Search } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

export default function ReassignLocation() {
  const navigate = useNavigate();
  const [bundles, setBundles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [designFilter, setDesignFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const [assignOpen, setAssignOpen] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [binInput, setBinInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '500');
      if (designFilter.trim()) params.set('design_name', designFilter.trim());
      if (sourceFilter.trim()) params.set('source_section', sourceFilter.trim());
      const res = await apiClient.get(`/wms/sfg/blank-location-bundles?${params}`);
      setBundles(res.data?.bundles || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load bundles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openAssign = (bundle) => {
    setSelectedBundle(bundle);
    setBinInput('');
    setAssignOpen(true);
  };

  const submitAssign = async () => {
    if (!selectedBundle || !binInput.trim()) {
      toast.error('Please enter a bin code');
      return;
    }
    setSubmitting(true);
    try {
      const res = await apiClient.post('/wms/sfg/reassign-location', {
        bundle_code: selectedBundle.bundle_code,
        bin_code: binInput.trim()
      });
      toast.success(res.data?.message || 'Bundle reassigned');
      setAssignOpen(false);
      setSelectedBundle(null);
      setBinInput('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reassign');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="reassign-location-back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-[#1A1F2E]">Reassign Location</h1>
              <p className="text-sm text-gray-600">
                STORED bundles with blank bin locations — assign them to an SFG bin.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="reassign-location-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <CardTitle className="text-base">
                Pending Reassignment ({total.toLocaleString()})
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Filter by design"
                    value={designFilter}
                    onChange={(e) => setDesignFilter(e.target.value)}
                    className="pl-8 w-56"
                    data-testid="reassign-location-design-filter"
                  />
                </div>
                <Input
                  placeholder="Filter by source section"
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="w-52"
                  data-testid="reassign-location-source-filter"
                />
                <Button
                  variant="secondary"
                  onClick={load}
                  disabled={loading}
                  data-testid="reassign-location-apply-filter-btn"
                >
                  Apply
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-10 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
              </div>
            ) : bundles.length === 0 ? (
              <div className="py-10 text-center text-gray-500">
                No bundles with blank location. 🎉
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bundle</TableHead>
                      <TableHead>Design / Shade / Size</TableHead>
                      <TableHead>USNs</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Released</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bundles.map((b) => (
                      <TableRow key={b.sfg_id || b.bundle_code}>
                        <TableCell className="font-mono text-sm">
                          {b.bundle_code}
                          {b.sku_code && (
                            <div className="text-xs text-gray-500">{b.sku_code}</div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div className="font-medium">{b.design_name || '—'}</div>
                          <div className="text-xs text-gray-500">
                            {[b.shade, b.size, b.description].filter(Boolean).join(' · ') || '—'}
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold">{b.usn_count}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {b.source_section || '—'}
                          </Badge>
                          {b.floor_stock_revert_reason && (
                            <div className="text-xs text-gray-500 mt-1">
                              {b.floor_stock_revert_reason}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-gray-600">
                          {b.floor_stock_released_at
                            ? new Date(b.floor_stock_released_at).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            onClick={() => openAssign(b)}
                            data-testid={`reassign-location-assign-btn-${b.bundle_code}`}
                          >
                            <MapPin className="h-4 w-4 mr-1" />
                            Assign Bin
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Assign Bin Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="reassign-location-dialog">
          <DialogHeader>
            <DialogTitle>Assign SFG Bin</DialogTitle>
          </DialogHeader>
          {selectedBundle && (
            <div className="space-y-3">
              <div className="rounded bg-gray-50 p-3 text-sm">
                <div><span className="text-gray-500">Bundle:</span> <span className="font-mono">{selectedBundle.bundle_code}</span></div>
                <div><span className="text-gray-500">Design:</span> {selectedBundle.design_name || '—'}</div>
                <div><span className="text-gray-500">USNs:</span> {selectedBundle.usn_count}</div>
              </div>
              <div>
                <Label htmlFor="bin-code">Bin Code</Label>
                <Input
                  id="bin-code"
                  placeholder="e.g. SFG-A-01-L1"
                  value={binInput}
                  onChange={(e) => setBinInput(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitAssign(); }}
                  autoFocus
                  data-testid="reassign-location-bin-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  The bin must exist in the SFG storage master.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignOpen(false)}
              disabled={submitting}
              data-testid="reassign-location-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitAssign}
              disabled={submitting || !binInput.trim()}
              data-testid="reassign-location-submit-btn"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
